import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { uiStore } from 'shared/store/uiStore'
import type { IParamsSearchMessages } from 'entities/Message'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import { ConversationRoutes } from 'pages/conversations/route/type'

type ISearchInputStoreConfig = {
  onBlur: (value: string) => void
  onSearch: (value: string) => void
}

export class SearchInputStore {
  private _search = ''
  private _focusTrigger = ''
  private _config: ISearchInputStoreConfig | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  initSearch = () => {
    const params = new URLSearchParams(window.location.search)
    const search = params.get('search')

    if (search) {
      this.setSearch(search)
    }
  }

  setConfig = (config: ISearchInputStoreConfig) => {
    this._config = config
  }

  setSearch = (search: string) => {
    this._search = search
    this._config?.onSearch(search)
  }

  setFocusTrigger = () => {
    this._focusTrigger = nanoid()
  }

  onClear = () => {
    this.setSearch('')
    this.setFocusTrigger()

    logV2SearchEvent(
      'Search Page Clear Clicked',
      {
        event_id: 'search_page_clear_clicked',
      },
      this._entities
    )
  }

  onClose = () => {
    uiStore.changeRoute({ path: `/${ConversationRoutes.root}` })

    logV2SearchEvent(
      'Search Page Close Icon Clicked',
      {
        event_id: 'search_page_close_icon_clicked',
      },
      this._entities
    )
  }

  createRecentSearch = () => {
    this._config?.onBlur(this._search.trim())
  }

  dispose = () => {
    this._search = ''
    this._focusTrigger = ''
    this._config = null
  }

  get search() {
    return this._search
  }

  get isClear() {
    return !!this._search.trim()
  }

  get focusTrigger() {
    return this._focusTrigger
  }

  get params(): Omit<IParamsSearchMessages, 'page' | 'per_page'> | null {
    const term = this._search.trim()
    const params = {
      term: term.length > 2 ? term : undefined,
    }
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => Boolean(value))
    )
    if (Object.keys(filteredParams).length) {
      return filteredParams
    }
    return null
  }
}
